/**
 * Iter Feb 2026 · InlinePMEditor
 *
 * Small popover picker that lets a manager reassign a project's Project
 * Manager without opening the project detail page. Used from the
 * Projects list PM column.
 *
 * Contract:
 *   • `project` — the row (must have id, workspace_id, project_manager_id).
 *   • `members` — list of workspace members `{user_id, name, email}` (fetched
 *     once by the parent and shared across rows to avoid N+1 calls).
 *   • `onSaved(updatedRow)` — parent mutates local state after PATCH.
 *   • `canEdit` — boolean gate (workspace admin / project.manage).
 */
import { useMemo, useState } from "react";
import api from "@/lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Search, Check, Pencil, Loader2 } from "lucide-react";

export default function InlinePMEditor({ project, members = [], onSaved, canEdit = true }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const currentName = project.pm_name || project.project_manager_name
    || project.owner_name || project.created_by_name;
  const currentId = project.project_manager_id || project.pm_id || project.owner_id;

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = (members || []).map((m) => ({
      id: m.user_id || m.id,
      name: m.user_name || m.name || m.email || "—",
      email: m.email || "",
    })).filter((m) => m.id);
    if (!term) return list;
    return list.filter((m) =>
      (m.name + " " + m.email).toLowerCase().includes(term)
    );
  }, [members, q]);

  const pick = async (uid, name) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.patch(`/projects/${project.id}`, { project_manager_id: uid });
      const updated = {
        ...project,
        project_manager_id: uid,
        pm_id: uid,
        pm_name: name,
        project_manager_name: name,
      };
      onSaved?.(updated);
      toast.success(`Project Manager set to ${name}`);
      setOpen(false);
      setQ("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update PM");
    } finally {
      setBusy(false);
    }
  };

  if (!canEdit) {
    return (
      <div className="min-w-0 text-xs truncate" data-testid={`proj-pm-${project.id}`}>
        {currentName || <span className="text-muted-foreground italic">—</span>}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQ(""); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="group/pm inline-flex items-center gap-1 text-xs truncate hover:bg-muted px-1 -mx-1 py-0.5 w-full text-left"
          data-testid={`proj-pm-edit-trigger-${project.id}`}
          title="Click to reassign Project Manager">
          <span className="truncate flex-1">
            {currentName || <span className="text-muted-foreground italic">—</span>}
          </span>
          <Pencil className="w-2.5 h-2.5 opacity-0 group-hover/pm:opacity-70 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-72 p-0 rounded-none border border-border"
        onClick={(e) => e.stopPropagation()}
        data-testid={`proj-pm-edit-popover-${project.id}`}>
        <div className="p-2 border-b border-border">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 opacity-60 pointer-events-none" />
            <Input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search team…"
              className="h-7 text-[11px] rounded-none pl-6"
              data-testid={`proj-pm-edit-search-${project.id}`}
            />
          </div>
          <div className="mt-1 text-[9px] uppercase tracking-widest text-muted-foreground">
            Reassign PM · saves immediately
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto">
          <button
            type="button"
            disabled={busy}
            onClick={() => pick(null, null)}
            className="w-full text-left px-2 py-1.5 text-[12px] border-b border-border hover:bg-muted inline-flex items-center gap-2 italic text-muted-foreground disabled:opacity-60"
            data-testid={`proj-pm-edit-unassign-${project.id}`}>
            <Check className={"w-3 h-3 " + (!currentId ? "opacity-100" : "opacity-0")} />
            Unassigned
          </button>
          {rows.length === 0 ? (
            <div className="px-3 py-4 text-[11px] text-muted-foreground">
              {q ? "No matches." : "No workspace members."}
            </div>
          ) : (
            rows.map((m) => {
              const isCur = currentId === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  disabled={busy}
                  onClick={() => pick(m.id, m.name)}
                  className={"w-full text-left px-2 py-1.5 text-[12px] hover:bg-muted inline-flex items-center gap-2 disabled:opacity-60 " + (isCur ? "bg-muted font-semibold" : "")}
                  data-testid={`proj-pm-edit-option-${project.id}-${m.id}`}>
                  <Check className={"w-3 h-3 shrink-0 " + (isCur ? "opacity-100" : "opacity-0")} />
                  <span className="truncate flex-1">{m.name}</span>
                  {busy && isCur && <Loader2 className="w-3 h-3 animate-spin" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
